import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const chain = [
  { key: "sleep", label: "Poor sleep", detail: "5.2h · low HRV", color: "#C4B5FD", icon: "moon" },
  { key: "crave", label: "Sugar cravings", detail: "+42% vs baseline", color: "#FB7185", icon: "heart" },
  { key: "glucose", label: "Glucose spike", detail: "168 mg/dL @ 4pm", color: "#FDA4AF", icon: "spike" },
  { key: "fatigue", label: "Afternoon crash", detail: "Focus ↓ 38%", color: "#2DD4BF", icon: "battery" },
];

const Ic = ({ name, color }) => {
  const c = { width: 20, height: 20, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  const m = {
    moon: <svg {...c}><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>,
    heart: <svg {...c}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z"/></svg>,
    spike: <svg {...c}><path d="M3 18l4-10 3 6 4-12 3 8h4"/></svg>,
    battery: <svg {...c}><rect x="3" y="8" width="16" height="8" rx="2"/><path d="M21 11v2"/><path d="M7 12h3"/></svg>,
  };
  return m[name];
};

export default function SignatureUSP() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % chain.length), 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="usp" data-testid="usp-section" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute left-1/4 top-1/3 h-[520px] w-[520px] bg-radial-lav opacity-70" />
        <div className="absolute right-1/4 bottom-1/4 h-[420px] w-[420px] bg-radial-coral opacity-60" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.22em] text-teal-soft">04 — Signature intelligence</div>
          <h2 className="mt-4 font-heading text-4xl sm:text-5xl tracking-tighter leading-[1.05] font-medium">
            Food <span className="gradient-text-teal-lav">↔</span> Biomarker <span className="gradient-text-teal-lav">↔</span> Mood.
            <br /><span className="text-zinc-400">The chain nobody was watching.</span>
          </h2>
          <p className="mt-5 text-zinc-400 max-w-2xl">
            Most apps show you one number. HealthIRL shows you the <span className="text-white">causal chain</span> — the sequence that explains why your afternoon
            just fell apart.
          </p>
        </div>

        <div className="mt-14 relative rounded-3xl border border-white/[0.08] glass-strong p-6 sm:p-10 overflow-hidden">
          {/* Flow */}
          <div className="relative grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-3">
            {chain.map((n, i) => {
              const isActive = i === active;
              const wasActive = i < active;
              return (
                <div key={n.key} className="relative">
                  <motion.div
                    animate={{ scale: isActive ? 1.03 : 1, opacity: isActive || wasActive ? 1 : 0.55 }}
                    transition={{ duration: 0.5 }}
                    data-testid={`usp-node-${n.key}`}
                    className={`relative rounded-2xl border p-5 h-full transition-colors ${
                      isActive ? "border-white/25 bg-white/[0.06]" : "border-white/[0.08] bg-white/[0.02]"
                    }`}
                    style={{
                      boxShadow: isActive ? `0 20px 60px -20px ${n.color}55, inset 0 0 0 1px ${n.color}55` : "none",
                    }}>
                    <div className="flex items-center justify-between">
                      <div className="h-10 w-10 rounded-lg bg-white/[0.03] flex items-center justify-center"
                           style={{ border: `1px solid ${n.color}44` }}>
                        <Ic name={n.icon} color={n.color} />
                      </div>
                      <span className="text-[10px] font-mono text-zinc-500">0{i + 1}</span>
                    </div>
                    <div className="mt-4 font-heading text-lg tracking-tight">{n.label}</div>
                    <div className="mt-1 text-[11px] uppercase tracking-wider" style={{ color: n.color }}>{n.detail}</div>
                  </motion.div>

                  {i < chain.length - 1 && (
                    <div className="hidden md:flex absolute top-1/2 -right-2 -translate-y-1/2 z-10">
                      <motion.svg width="20" height="14" viewBox="0 0 20 14" fill="none"
                        animate={{ opacity: i < active ? 1 : 0.35, x: i === active - 1 ? [0, 4, 0] : 0 }}
                        transition={{ duration: 0.8, repeat: i === active - 1 ? Infinity : 0 }}>
                        <path d="M0 7 L18 7 M13 2 L18 7 L13 12" stroke={n.color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                      </motion.svg>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Insight strip */}
          <div className="mt-8 rounded-2xl border border-lav-500/25 bg-lav-500/[0.05] p-5">
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-md bg-lav-500/25 flex items-center justify-center shrink-0">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C4B5FD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3 2 5 5 2-5 2-2 5-2-5-5-2 5-2z"/></svg>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-lav-400">AI reasoning</div>
                <AnimatePresence mode="wait">
                  <motion.p key={active}
                    initial={{ y: 6, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -4, opacity: 0 }}
                    transition={{ duration: 0.4 }} className="mt-1 text-sm sm:text-base text-zinc-100 leading-relaxed">
                    {active === 0 && "Your 3-night sleep debt dropped HRV to 38ms — the exact biological setup for tomorrow's cravings."}
                    {active === 1 && "Cortisol-driven cravings for fast glucose are 42% above your baseline. Fully predicted from last night's sleep."}
                    {active === 2 && "A 168 mg/dL post-lunch spike is now traced back to poor sleep + high-GI snack — the causal chain is complete."}
                    {active === 3 && "The 4pm crash isn't willpower — it's insulin overshoot. Fix the trigger (sleep), not the symptom."}
                  </motion.p>
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Dots */}
          <div className="mt-5 flex items-center justify-center gap-2">
            {chain.map((_, i) => (
              <button key={i} onClick={() => setActive(i)} data-testid={`usp-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${i === active ? "w-8 bg-teal-soft" : "w-1.5 bg-white/15"}`}
                aria-label={`Show step ${i + 1}`} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
